import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMeeting, useAuth, API } from "../App";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Building2, Zap, Loader2, Plus, Search } from "lucide-react";
import { toast } from "sonner";

const CustomerIntakeModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { createMeeting } = useMeeting();
  const [isLoading, setIsLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    customerName: "",
    company: "",
    primaryAssetFocus: "",
    contactPerson: "",
    email: "",
    phone: ""
  });

  useEffect(() => {
    if (isOpen && token) {
      fetchCustomers();
    }
  }, [isOpen, token]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    }
  };

  const assetOptions = [
    { value: "Transformers", label: "Transformers" },
    { value: "Switchgear", label: "Switchgear" },
    { value: "Motors", label: "Motors" }
  ];

  const handleCustomerSelect = (customerId) => {
    if (customerId === "new") {
      setIsNewCustomer(true);
      setSelectedCustomerId("");
      setFormData({ ...formData, customerName: "", company: "" });
    } else {
      setIsNewCustomer(false);
      setSelectedCustomerId(customerId);
      const customer = customers.find(c => c.id === customerId);
      if (customer) {
        setFormData({
          ...formData,
          customerName: customer.contact_person || customer.name,
          company: customer.name
        });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company || !formData.primaryAssetFocus) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      setIsLoading(true);
      
      let customerId = selectedCustomerId;
      
      // Create new customer if:
      // 1. User explicitly chose "new customer" (isNewCustomer=true), OR
      // 2. No customers exist (customers.length === 0) and no customer is selected
      // AND company name is provided
      const shouldCreateCustomer = (isNewCustomer || (customers.length === 0 && !selectedCustomerId)) && formData.company;
      
      if (shouldCreateCustomer) {
        const newCustomer = await axios.post(`${API}/customers`, {
          name: formData.company,
          contact_person: formData.customerName,
          email: formData.email,
          phone: formData.phone
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        customerId = newCustomer.data.id;
        toast.success("New customer created");
      }
      
      const meeting = await createMeeting(
        formData.customerName || formData.company,
        formData.company,
        formData.primaryAssetFocus,
        customerId
      );
      toast.success("Session started successfully");
      // Reset form
      setFormData({ customerName: "", company: "", primaryAssetFocus: "", contactPerson: "", email: "", phone: "" });
      setSelectedCustomerId("");
      setIsNewCustomer(false);
      onClose();
      // Navigate after a short delay to ensure state is updated
      setTimeout(() => {
        navigate("/dashboard");
      }, 100);
    } catch (error) {
      toast.error("Failed to create session");
      console.error(error);
      setIsLoading(false);
    }
  };

  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.contact_person && c.contact_person.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[520px]" data-testid="customer-intake-modal">
        <DialogHeader>
          <DialogTitle className="text-heading text-2xl text-slate-900">
            Start New Session
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Select an existing customer or create a new one.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          {/* Customer Selection */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
              Customer
            </Label>
            {customers.length > 0 && !isNewCustomer ? (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search customers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 h-10"
                  />
                </div>
                <div className="max-h-40 overflow-y-auto border rounded-md">
                  {filteredCustomers.map((customer) => (
                    <button
                      key={customer.id}
                      type="button"
                      onClick={() => handleCustomerSelect(customer.id)}
                      className={`w-full text-left px-3 py-2 hover:bg-slate-100 border-b last:border-b-0 transition-colors ${
                        selectedCustomerId === customer.id ? "bg-blue-50 border-blue-200" : ""
                      }`}
                    >
                      <p className="font-medium text-sm">{customer.name}</p>
                      {customer.contact_person && (
                        <p className="text-xs text-slate-500">{customer.contact_person}</p>
                      )}
                    </button>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleCustomerSelect("new")}
                  className="w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create New Customer
                </Button>
              </div>
            ) : (
              <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                {customers.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsNewCustomer(false)}
                    className="text-blue-600 p-0 h-auto"
                  >
                    ← Select existing customer
                  </Button>
                )}
                <div>
                  <Label className="text-xs">Company Name *</Label>
                  <Input
                    placeholder="PowerGrid Solutions Inc."
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="h-10 mt-1"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs">Contact Person</Label>
                  <Input
                    placeholder="John Smith"
                    value={formData.customerName}
                    onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                    className="h-10 mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input
                      type="email"
                      placeholder="john@company.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="h-10 mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input
                      placeholder="+1 234 567 8900"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="h-10 mt-1"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Show selected customer info */}
          {selectedCustomerId && !isNewCustomer && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm font-medium text-blue-900">
                {customers.find(c => c.id === selectedCustomerId)?.name}
              </p>
              <p className="text-xs text-blue-700">
                {customers.find(c => c.id === selectedCustomerId)?.contact_person || "No contact person"}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="assetFocus" className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
              Primary Asset Focus
            </Label>
            <select
              id="assetFocus"
              value={formData.primaryAssetFocus}
              onChange={(e) => setFormData({ ...formData, primaryAssetFocus: e.target.value })}
              className="flex h-11 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="asset-focus-select"
            >
              <option value="">Select primary asset type</option>
              {assetOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              disabled={isLoading}
              data-testid="start-session-submit-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                "Start Session"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerIntakeModal;
